import { useLazoView } from "@/contexts/CurrentLazoViewContext";
import { Colors } from "@/enums/Colors";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useLazoFilePreSignedUrl } from "@/hooks/queries/lazo-file/useLazoFileQueries";
import { DocumentCenterFileSmall } from "@/models/DocumentCenterFileSmall";
import { LazoFile } from "@/models/LazoFile";
import DocumentCenterFileService from "@/services/DocumentCenterFileService";
import LazoFileService from "@/services/LazoFileService";
import { downloadFileFromPreSignedUrl } from "@/utils/LazoFIleUtils";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { CircularProgress, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import {
  ActionButton,
  ActionCard,
  ActionTitle,
  BaseDocumentViewerModal,
  FilePreviewContainer,
  FilePreviewLoading,
  InfoItem,
  InfoLabel,
  InfoValue,
} from "../common/BaseDocumentViewerModal";
import FilePreview from "../common/FilePreview";
import { handleFileTypeByName } from "../common/FilePreviewPopOver";
import FileBrowserRecursiveListWrapper from "../document-center-components/FileBrowserRecursiveListWrapper";
import CommonButton from "../utils/CommonButton";
import LazoModal from "../utils/LazoModal";
import { DownloadIcon } from "../utils/icons/DownloadIcon";

interface LazoFileViewerModalProps {
  open: boolean;
  onClose: () => void;
  file: LazoFile | null;
  tenantExternalId?: string;
}

export function LazoFileViewerModal({ open, onClose, file, tenantExternalId }: LazoFileViewerModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSendingToDocCenter, setIsSendingToDocCenter] = useState(false);
  const [openFolderSelector, setOpenFolderSelector] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DocumentCenterFileSmall>(null);
  const [editedFileName, setEditedFileName] = useState("");
  const { isPortal } = useLazoView();

  const fileType = useMemo(() => {
    if (!file?.name) return "other";
    return handleFileTypeByName(file.name);
  }, [file?.name]);

  // Load pre-signed URL only when modal is opened
  const { data: fileUrl, isLoading: loadingUrl } = useLazoFilePreSignedUrl(file?.externalId, open);

  async function handleDownload() {
    if (!file) return;

    setIsDownloading(true);
    const response = await LazoFileService.generatePreSignedUrl(file.externalId);

    response.onSuccess((preSignedUrl: string) => {
      downloadFileFromPreSignedUrl(preSignedUrl, file.name);
    });

    response.onError(() => {
      // Error already handled by service
    });

    setIsDownloading(false);
  }

  function handleOpenFolderSelector() {
    const nameWithoutExt = file?.name ? removeExtension(file.name) : "";
    setEditedFileName(nameWithoutExt);
    setOpenFolderSelector(true);
  }

  function handleCloseFolderSelector() {
    setOpenFolderSelector(false);
    setSelectedFolder(null);
    setEditedFileName("");
  }

  function removeExtension(name: string) {
    const dotIndex = name.lastIndexOf(".");
    return dotIndex > 0 ? name.slice(0, dotIndex) : name;
  }

  async function handleSendToDocumentCenter() {
    if (!file || !selectedFolder || !tenantExternalId) return;

    setIsSendingToDocCenter(true);

    try {
      const response = await DocumentCenterFileService.copyTaskFileToFolder(
        file.externalId,
        tenantExternalId,
        selectedFolder.externalId,
        editedFileName || undefined
      );

      response.onSuccess(() => {
        NotificationService.info("File sent to Document Center successfully");
        handleCloseFolderSelector();
      });

      response.onError((message) => {
        NotificationService.error(message || "Error sending file to Document Center");
      });
    } finally {
      setIsSendingToDocCenter(false);
    }
  }

  const filePreviewContent =
    loadingUrl || !fileUrl ? (
      <FilePreviewLoading />
    ) : (
      <FilePreviewContainer>
        <FilePreview fileUrl={fileUrl} fileType={fileType} isPreviewModal viewportHeight="100%" viewportWidth="100%" hideToolbar />
      </FilePreviewContainer>
    );

  const actionsContent = (
    <>
      {/* Document Info */}
      <ActionCard>
        <ActionTitle>File Information</ActionTitle>
        <InfoItem>
          <InfoLabel>Uploaded by</InfoLabel>
          <InfoValue>
            {file?.submitterUser?.firstName} {file?.submitterUser?.lastName}
          </InfoValue>
        </InfoItem>
        <InfoItem>
          <InfoLabel>Date</InfoLabel>
          <InfoValue>{file?.createdAt ? new Date(file.createdAt).toLocaleDateString() : "-"}</InfoValue>
        </InfoItem>
        {file?.size && (
          <InfoItem>
            <InfoLabel>Size</InfoLabel>
            <InfoValue>{(file.size / 1000).toFixed(2)} KB</InfoValue>
          </InfoItem>
        )}
      </ActionCard>

      {/* Quick Actions */}
      <ActionCard>
        <ActionTitle>Actions</ActionTitle>
        <ActionButton variant="outlined" onClick={handleDownload} disabled={isDownloading}>
          <DownloadIcon sx={{ width: "16px", height: "16px", fill: Colors.BLACK }} />
          {isDownloading ? <CircularProgress size={16} /> : "Download"}
        </ActionButton>
        {tenantExternalId && isPortal && (
          <ActionButton variant="outlined" onClick={handleOpenFolderSelector} disabled={isSendingToDocCenter}>
            <FolderOpenIcon sx={{ width: "16px", height: "16px", fill: Colors.BLACK }} />
            Send to Document Center
          </ActionButton>
        )}
      </ActionCard>
    </>
  );

  return (
    <>
      <BaseDocumentViewerModal
        open={open}
        onClose={onClose}
        title={file?.name || "File Viewer"}
        filePreviewContent={filePreviewContent}
        actionsContent={actionsContent}
      />

      {/* Folder Selector Modal */}
      <LazoModal
        open={openFolderSelector}
        title="Select folder in Document Center"
        handleClose={handleCloseFolderSelector}
        sx={{ maxWidth: "400px" }}
        buttons={[
          <CommonButton
            key="cancel"
            purpleBorderVariant
            disabled={isSendingToDocCenter}
            sx={{ borderRadius: "0.5rem" }}
            onClick={handleCloseFolderSelector}
          >
            Cancel
          </CommonButton>,
          <CommonButton
            key="confirm"
            disabled={isSendingToDocCenter || !selectedFolder}
            sx={{ borderRadius: "0.5rem" }}
            onClick={handleSendToDocumentCenter}
          >
            {isSendingToDocCenter ? <CircularProgress size={16} /> : "Send"}
          </CommonButton>,
        ]}
      >
        <TextField
          label="File name"
          value={editedFileName}
          onChange={(e) => setEditedFileName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2, mt: 2 }}
          helperText={`Extension will be preserved: ${file?.name ? file.name.slice(file.name.lastIndexOf(".")) : ""}`}
        />
        {tenantExternalId && (
          <FileBrowserRecursiveListWrapper
            tenantExternalId={tenantExternalId}
            setSelectedFolder={setSelectedFolder}
            selectedFolder={selectedFolder}
          />
        )}
      </LazoModal>
    </>
  );
}
